import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from './i18n';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

// Product name input with suggestions from the catalog (GET /api/products?category=&q=).
// Props: category ('coffee' | 'tea' | 'oil' | 'vinegar'), value (the product name typed so far),
// onChange(fields): called with { product_name } while typing and, when a suggestion is picked, with
// { product_name, producer, origin, variety, harvest_year } (missing values as '' so the form can overwrite its fields);
// optional required, placeholder.
export default function ProductAutocomplete({ category, value, onChange, required = false, placeholder }) {
  const { t } = useI18n();
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 2) { setSuggestions([]); return; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/products?category=${encodeURIComponent(category)}&q=${encodeURIComponent(value)}&limit=8`,
          { signal: controller.signal });
        if (res.ok) setSuggestions((await res.json()).slice(0, 8));
      } catch { /* aborted */ }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [value, category]);

  useEffect(() => {
    const onDocumentClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const pick = (product) => {
    onChange({
      product_name: product.name, producer: product.producer || '', origin: product.origin || '',
      variety: product.variety || '', harvest_year: product.harvest_year || '',
    });
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && highlighted >= 0) { e.preventDefault(); pick(suggestions[highlighted]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="autocomplete-wrap" ref={wrapRef}>
      <input
        value={value}
        onChange={(e) => { onChange({ product_name: e.target.value }); setOpen(true); setHighlighted(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || t('products.name_placeholder')}
        required={required}
        maxLength={200}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="autocomplete-list">
          {suggestions.map((s, i) => (
            <li key={s.id}
                className={i === highlighted ? 'highlighted' : ''}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}>
              <strong>{s.name}</strong>
              {s.harvest_year ? <span className="ac-year"> {s.harvest_year}</span> : ''}
              {s.producer ? <span className="ac-prod"> — {s.producer}</span> : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
